import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Trash2 } from 'lucide-react';
import {
  assignVendorNotesMessage,
  assignVendorToPurchaseOrder,
  assignVendorToPurchaseOrderErrorMessage,
  deletePurchaseOrderErrorMessage,
  deleteVendorPurchaseOrder,
  fetchVendorPurchaseOrders,
  PRICES_EXCLUDE_VAT,
  unpricedLineNote,
  type VendorPurchaseOrderDto,
} from '../lib/vendorPurchaseOrderApi';
import { isUnresolvedVendorPo } from '../features/vendorPurchaseOrders/poVendorGaps';
import { VendorPoExportPicker } from '../features/vendorPurchaseOrders/VendorPoExportPicker';
import { VendorSelect, NO_VENDOR } from '../components/VendorSelect';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import LoadingState from '../components/LoadingState';
import StatusBadge from '../components/StatusBadge';
import VendorBatchSendModal from '../components/VendorBatchSendModal';
import ActionDialog from '../components/ActionDialog';
import ErrorModal from '../components/ErrorModal';
import Button from '../components/ui/Button';
import Pagination from '../components/ui/Pagination';
import SearchInput from '../components/ui/SearchInput';
import { EmptyState, ErrorState } from '../components/ui/States';
import { usePermission } from '../hooks/usePermission';
import { formatDate as fmtDate } from "../utils/dateFormat";
import { toCsv } from '../utils/csv';
import { clientDisplayName, orderRef } from '../utils/orderIdentity';
import { formatCurrency } from '../utils/priceUtils';
import DateInput from "../components/DateInput";

const VENDOR_PO_INFO_CONTENT = (
  <div className="flex flex-col gap-3">
    <p>Vendor Purchase Orders (POs) are created from confirmed orders and sent to local vendors in TT$.</p>
    <p className="font-medium">From this screen you can:</p>
    <ul className="list-disc list-inside space-y-1 pl-1">
      <li>View POs grouped by Confirmed Order with one row per vendor, showing the client and the vessel the goods are for</li>
      <li>Open the Confirmed Order detail page from the Confirmed Order link</li>
      <li>Export one PO as the PDF or Excel its supplier gets (or the list as a CSV), or open, edit, or delete a PO</li>
    </ul>
    <p>POs are generated from the Confirmed Order screen.</p>
  </div>
);

const coRef = (row: VendorPurchaseOrderDto) =>
  (row.confirmedOrderId && typeof row.confirmedOrderId === 'object') ? row.confirmedOrderId : null;

// Grouping key: the confirmed order _id (fall back to a stable string).
const coKey = (row: VendorPurchaseOrderDto) => {
  const ref = coRef(row);
  if (ref?._id) return ref._id;
  if (typeof row.confirmedOrderId === 'string') return row.confirmedOrderId;
  return `__none_${row._id}`;
};

// Human label for the cell. The vessel used to sit in the middle of this
// fallback chain, which put a ship's name in a column headed "Confirmed Order";
// it has its own column now, and the order's identity comes from the shared
// `orderRef` (QUO number first, owner decision 2026-10-02) so this screen and the
// Confirmed Order list name an order the same way.
const coDisplay = (row: VendorPurchaseOrderDto) => orderRef(coRef(row)) || '—';

/**
 * Who the order is FOR. Deborah, 2026-09-04: this list had neither the client
 * nor the vessel, so a buyer looking at a purchase order could not tell which
 * ship it was being bought for without opening the confirmed order behind it.
 *
 * Both come off documents the list endpoint already populates
 * (`confirmedOrderId` → clientName/vesselName, `rfqId` → customerName/
 * vesselName), so neither column costs a request per row.
 */
const clientDisplay = (row: VendorPurchaseOrderDto) => {
  const fromOrder = clientDisplayName(coRef(row)?.clientName);
  if (fromOrder) return fromOrder;
  const rfq = row.rfqId;
  if (rfq && typeof rfq === 'object') return clientDisplayName(rfq.customerName) || '—';
  return '—';
};

// The PO's OWN copy first: it is what `VPO_SORT_FIELDS.vessel` (utils/vendorPoSort.js) sorts on, and a
// column that shows one value while the sort orders by another is its own bug.
// `vesselAssignment.service.js` keeps that copy in step when the vessel is
// corrected upstream, so "current" and "what the sort sees" are the same string.
const vesselDisplay = (row: VendorPurchaseOrderDto) => {
  const own = String(row.vesselName || '').trim();
  if (own) return own;
  const fromOrder = String(coRef(row)?.vesselName || '').trim();
  if (fromOrder) return fromOrder;
  const rfq = row.rfqId;
  if (rfq && typeof rfq === 'object' && rfq.vesselName) return String(rfq.vesselName).trim();
  return '—';
};

const coMongoId = (row: VendorPurchaseOrderDto) => coRef(row)?._id || null;

const formatDateUs = (value?: string) => {
  return fmtDate(value, '?');
};

function exportVendorPoCsv(rows: VendorPurchaseOrderDto[]) {
  // Same columns as the table, in the same order. The first header used to say
  // "RFQ ID" while the column above it said "Confirmed Order" and rendered a
  // different function — a header array disagreeing with its rows.
  const headers = [
    'Confirmed Order', 'Client Name', 'Vessel Name', 'Vendor Name',
    'Created', 'Total Items', 'Total Price (TTD)', 'Unpriced Lines', 'Price Basis', 'PO Number',
  ];
  const lines = rows.map((row) => {
    const cells = [
      coDisplay(row),
      clientDisplay(row),
      vesselDisplay(row),
      row.vendorName || '',
      formatDateUs(row.createdAt),
      String(row.totalItems ?? 0),
      row.totalPrice != null && Number.isFinite(row.totalPrice) ? String(row.totalPrice) : '',
      // TECH_DEBT #342 — Total Price (TTD) above already excludes any line with
      // no agreed price yet; prose saying so belongs on the SCREEN's total, not
      // in a numeric CSV column someone else's spreadsheet will sum. A numeric
      // column instead: 0 when every line is priced, so a formula summing this
      // column reads "how many rows need a follow-up", not silence.
      String(row.unpricedLineCount || 0),
      // D82: a before-VAT PO's total and a pre-D82 (VAT-inclusive) one share the
      // Total Price column, so the basis rides in its own column a spreadsheet
      // can filter on before it sums. Read from the stored priceBasis, never inferred.
      row.priceBasis === 'before_vat' ? PRICES_EXCLUDE_VAT : '',
      row.poNumber || '',
    ];
    return cells;
  });
  // toCsv, not a private escaper — see utils/csv. The BOM stays: without it
  // Excel on Windows reads UTF-8 as the system codepage. (TECH_DEBT #38)
  const csv = '\uFEFF' + toCsv([headers, ...lines]);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `vendor-purchase-orders-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const UNSENT_DISMISS_KEY = "vpoUnsentBannerDismissed";

export const VendorPurchaseOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const { canEdit, canDelete, canExport } = usePermission('vendor_purchase_orders');
  const [rows, setRows] = useState<VendorPurchaseOrderDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // The delete confirmation and its failure, in the app's own dialog rather
  // than window.confirm/window.alert. ActionDialog and ErrorModal directly,
  // NOT `useConfirm()`/`useToast()`: VendorPurchaseOrdersPage.test.tsx renders
  // this page bare, with no ConfirmProvider or ToastProvider around it, and
  // those hooks throw when their provider is missing.
  const [pendingDelete, setPendingDelete] = useState<VendorPurchaseOrderDto | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [total, setTotal] = useState(0);
  const [showByVendor, setShowByVendor] = useState(false);
  // Filters / sorting (server-side). (QA VPO filters ticket)
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const reqSeq = useRef(0);

  // A PO with no vendor record (`vendorId: null`) — the register just didn't
  // recognise the typed supplier name. Fixed in place with the shared supplier
  // picker (`VendorSelect`, D86/D107), which reads the one vendor list itself.
  const [assigningPoId, setAssigningPoId] = useState<string | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);
  // What a vendor assign did NOT do (server `meta.notes`, worded by code).
  const [assignNote, setAssignNote] = useState<string | null>(null);
  // Server-computed, across the WHOLE filtered set (every page), not derived
  // from the loaded page's rows — see `countUnresolvedVendorPos` on the
  // server. A page-scoped count would understate the real number whenever an
  // affected PO sits on a page that isn't currently open.
  const [unresolvedVendorCount, setUnresolvedVendorCount] = useState(0);

  // D104.31: the count the clerk dismissed; the banner returns only if it grows.
  const [dismissedUnsent, setDismissedUnsent] = useState(() => {
    try { return Number(window.localStorage.getItem(UNSENT_DISMISS_KEY)) || 0; } catch { return 0; }
  });
  const dismissUnsent = () => {
    setDismissedUnsent(unresolvedVendorCount);
    try { window.localStorage.setItem(UNSENT_DISMISS_KEY, String(unresolvedVendorCount)); } catch { /* storage blocked: dismissed for this view only */ }
  };

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchVendorPurchaseOrders(page, limit, debouncedSearch, {
        status: statusFilter, sortBy, sortDir, createdFrom: dateFrom, createdTo: dateTo,
      });
      if (seq !== reqSeq.current) return;
      setRows(data.vendorPurchaseOrders || []);
      setTotal(data.pagination?.total || 0);
      setUnresolvedVendorCount(data.unresolvedVendorCount || 0);
    } catch (e: unknown) {
      if (seq !== reqSeq.current) return;
      setError(e instanceof Error ? e.message : 'Failed to load vendor purchase orders');
      setRows([]);
      setTotal(0);
      setUnresolvedVendorCount(0);
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [page, limit, debouncedSearch, statusFilter, sortBy, sortDir, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setDebouncedSearch(search);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 whenever a filter/sort changes so we don't land on an out-of-range page.
  useEffect(() => {
    setPage(1);
  }, [statusFilter, sortBy, sortDir, dateFrom, dateTo]);

  const handleAssignVendor = async (poId: string, vendorId: string) => {
    if (!vendorId) return;
    setAssigningPoId(poId);
    setAssignError(null);
    try {
      const { notes } = await assignVendorToPurchaseOrder(poId, vendorId);
      // Said even if the refresh below fails: the vendor is already set.
      setAssignNote(assignVendorNotesMessage(notes));
      // Refresh from the server so the marker clears using the real,
      // server-written vendorName/vendorEmail — never optimistic-set from
      // whatever the dropdown option's label happened to say.
      await load();
    } catch (e: unknown) {
      setAssignError(assignVendorToPurchaseOrderErrorMessage(e));
    } finally {
      setAssigningPoId(null);
    }
  };

  // Search, filtering and sorting are all server-side now. Trust the server order (so the chosen
  // sort actually takes effect) — the rowspan grouping below still groups consecutive same-CO
  // rows, and the default date sort keeps a confirmed order's POs adjacent. (QA VPO filters)
  const sortedRows = rows;

  const { spanAtIndex, hideRfqAtIndex } = useMemo(() => {
    const spanAtIndex: Record<number, number> = {};
    const hideRfqAtIndex: Record<number, boolean> = {};
    for (let i = 0; i < sortedRows.length; i++) {
      const key = coKey(sortedRows[i]);
      if (i > 0 && coKey(sortedRows[i - 1]) === key) {
        hideRfqAtIndex[i] = true;
        continue;
      }
      let span = 1;
      for (let j = i + 1; j < sortedRows.length && coKey(sortedRows[j]) === key; j++) span += 1;
      spanAtIndex[i] = span;
    }
    return { spanAtIndex, hideRfqAtIndex };
  }, [sortedRows]);

  const handleExport = () => {
    exportVendorPoCsv(sortedRows);
  };

  const handleDelete = async () => {
    const row = pendingDelete;
    if (!row) return;
    setPendingDelete(null);
    setDeletingId(row._id);
    try {
      await deleteVendorPurchaseOrder(row._id);
      await load();
    } catch (err: unknown) {
      // By code (invariant 6): D81 keeps a PO the supplier has or an invoice points at.
      setDeleteError(deletePurchaseOrderErrorMessage(err, 'Delete failed'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="h-full flex flex-col bg-brand-bg">
      {/* The app's one header band: same gutter, same 20px title, same control
          heights as every other module (docs/DESIGN.md). */}
      <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 shrink-0">
            {/* The sidebar's own label for this screen, so the nav and the page agree. */}
            <h1 className="text-xl font-semibold leading-7 text-slate-900">Vendor Purchase Order</h1>
            <ModuleInfoButton content={VENDOR_PO_INFO_CONTENT} ariaLabel="Vendor Purchase Orders information" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <SearchInput value={search} onChange={setSearch} placeholder="Search here" className="w-48" />

            {/* Filters & sorting (QA VPO filters ticket) */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              title="Filter by status"
              className="h-9 px-2 rounded-md border border-slate-200 bg-white text-sm text-slate-700"
            >
              <option value="all">All statuses</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="amended">Amended — needs re-send</option>
              <option value="billed">Billed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select
              value={`${sortBy}:${sortDir}`}
              onChange={(e) => { const [f, d] = e.target.value.split(':'); setSortBy(f); setSortDir(d as 'asc' | 'desc'); }}
              title="Sort by"
              className="h-9 px-2 rounded-md border border-slate-200 bg-white text-sm text-slate-700"
            >
              <option value="date:desc">Newest first</option>
              <option value="date:asc">Oldest first</option>
              <option value="amount:desc">Amount: high to low</option>
              <option value="amount:asc">Amount: low to high</option>
              <option value="qty:desc">Qty: high to low</option>
              <option value="qty:asc">Qty: low to high</option>
              <option value="vendorName:asc">Vendor: A to Z</option>
              <option value="vessel:asc">Vessel: A to Z</option>
              <option value="delivery:asc">Delivery date: earliest</option>
              <option value="delivery:desc">Delivery date: latest</option>
              <option value="status:asc">Status</option>
            </select>
            <label className="flex items-center gap-1 h-9 px-2 rounded-md border border-slate-200 bg-white text-xs text-slate-500" title="Created from">
              From
              <DateInput validity="filter" value={dateFrom} max={dateTo || undefined} onChange={(v) => setDateFrom(v)} className="bg-transparent text-slate-700" />
            </label>
            <label className="flex items-center gap-1 h-9 px-2 rounded-md border border-slate-200 bg-white text-xs text-slate-500" title="Created to">
              To
              <DateInput validity="filter" value={dateTo} min={dateFrom || undefined} onChange={(v) => setDateTo(v)} className="bg-transparent text-slate-700" />
            </label>
            {(statusFilter !== 'all' || dateFrom || dateTo || sortBy !== 'date' || sortDir !== 'desc') && (
              <Button
                onClick={() => { setStatusFilter('all'); setSortBy('date'); setSortDir('desc'); setDateFrom(''); setDateTo(''); }}
              >
                Clear
              </Button>
            )}
            {canEdit && (
              <Button onClick={() => setShowByVendor(true)}>Send by vendor</Button>
            )}
            {/* Asks which PO, then downloads that PO's own PDF / Excel (Preet, 2026-10-20). */}
            {canExport && (
              <VendorPoExportPicker onExportList={handleExport} listEmpty={sortedRows.length === 0} />
            )}
          </div>
        </div>
      </div>

      {/* Persistent, reload-proof: a PO named a real supplier but never matched anyone
          in the vendor register, so it has no vendorId and cannot be sent. Derived
          from the loaded page's own rows (`poVendorGaps.ts`) — no new stored field, no
          new read endpoint. Pre-existing POs from before today's vendor-matching fix
          are included on purpose. */}
      {!loading && !error && unresolvedVendorCount > dismissedUnsent && (
        <div className="shrink-0 flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-1 sm:px-8">
          <p className="text-xs font-medium text-amber-800">
            {unresolvedVendorCount} purchase order{unresolvedVendorCount === 1 ? '' : 's'} cannot be sent — no vendor record
          </p>
          <button type="button" aria-label="Dismiss" onClick={dismissUnsent} className="ml-auto text-amber-700 hover:text-amber-900 text-sm leading-none">×</button>
        </div>
      )}


      <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <LoadingState text="Loading purchase orders..." />
            </div>
          ) : error ? (
            /* A load failure says so and offers the retry, instead of an empty
               table that reads as "there are no purchase orders". */
            <div className="p-4 sm:px-8">
              <ErrorState message={error} onRetry={load} />
            </div>
          ) : (
            <div className="h-full overflow-auto bg-white">
              {/* `data-table` (index.css) draws the header, the cells and the
                  row hover — the same table as every other module. */}
              <table className="data-table min-w-[1190px]">
                <thead>
                  <tr>
                    {/* No per-column filter icons: this screen filters and sorts
                        through the controls in the header band, and an icon on
                        every heading advertised a column menu that never existed. */}
                    <th className="w-[110px]">PO number</th>
                    <th className="w-[156px]">Confirmed order</th>
                    <th className="min-w-[160px]">Client name</th>
                    {/* Sortable server-side via the "Vessel: A to Z" option in
                        the sort select above, which orders by the PO's own
                        `vesselName` — the same value this column renders.

                        CATEGORY COLUMN INSERTION POINT: after this <th>, once
                        the `ItemCategory` vocabulary is settled. Adding it means
                        a <th> here, a <td> in the row, a cell in
                        `exportVendorPoCsv`'s header AND row, and the empty-state
                        colSpan below. */}
                    <th className="min-w-[150px]">Vessel name</th>
                    <th className="min-w-[200px]">Vendor name</th>
                    <th className="w-[112px]">Created</th>
                    <th className="w-[112px] text-right">Total items</th>
                    <th className="min-w-[140px] text-right">Total price</th>
                    {/* The PO's own status (`src/models/VendorPurchaseOrder.js`:
                        draft/sent/amended/cancelled), read the same way every
                        other module shows a status — `StatusBadge`, the one
                        status badge in the app (QA 2026-10-01). */}
                    <th className="w-[110px]">Status</th>
                    <th className="sticky right-0 w-[110px] bg-slate-50 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.length === 0 && (
                    <tr>
                      <td colSpan={10} className="px-4 py-10">
                        <EmptyState
                          title="No vendor purchase orders found"
                          detail="Try clearing the search or the filters above."
                        />
                      </td>
                    </tr>
                  )}
                  {sortedRows.map((row, rowIndex) => {
                    const showRfq = !hideRfqAtIndex[rowIndex];
                    const rowSpan = spanAtIndex[rowIndex] || 1;
                    return (
                      <tr key={row._id}>
                        <td className="whitespace-nowrap font-medium align-top">{row.poNumber || "—"}</td>
                        {showRfq && (
                          <td className="text-center align-top" rowSpan={rowSpan}>
                            {coMongoId(row) ? (
                              <Link
                                to={`/confirmed-orders/${coMongoId(row)}`}
                                className="font-medium text-primary-500 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {coDisplay(row)}
                              </Link>
                            ) : (
                              <span className="text-slate-500">{coDisplay(row)}</span>
                            )}
                          </td>
                        )}
                        {/* Client and vessel belong to the CONFIRMED ORDER, not
                            to the individual PO, so they share the same rowSpan
                            as the order column — repeating them once per vendor
                            would read as three different orders. */}
                        {showRfq && <td className="align-top" rowSpan={rowSpan}>{clientDisplay(row)}</td>}
                        {showRfq && <td className="align-top" rowSpan={rowSpan}>{vesselDisplay(row)}</td>}
                        <td className="align-top">
                          <div className="flex flex-col gap-1">
                            <span>{row.vendorName || '?'}</span>
                            {/* Per-row marker for `isUnresolvedVendorPo` — this PO named a real
                                supplier but has no vendorId, so it cannot be sent. The picker
                                fixes it in place: on success it re-fetches the list (`load()`)
                                using the server-written vendorName/email, which clears this
                                marker. */}
                            {isUnresolvedVendorPo(row) && (
                              <div className="flex flex-col gap-1">
                                <span className="text-xs font-medium text-amber-700 whitespace-normal">
                                  No vendor record
                                </span>
                                {canEdit && (
                                  <div onClick={(e) => e.stopPropagation()}>
                                    <VendorSelect
                                      compact
                                      allowClear={false}
                                      className="w-44"
                                      ariaLabel={`Set vendor for ${row.poNumber || 'this purchase order'}`}
                                      placeholder={assigningPoId === row._id ? 'Assigning...' : 'Set vendor...'}
                                      value={NO_VENDOR}
                                      disabled={assigningPoId === row._id}
                                      onChange={(next) => {
                                        if (next.vendorId) handleAssignVendor(row._id, next.vendorId);
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="whitespace-nowrap">{formatDateUs(row.createdAt)}</td>
                        <td className="text-right tabular-nums">{row.totalItems ?? 0}</td>
                        <td className="text-right tabular-nums whitespace-nowrap">
                          {/* The one label owner (BUSINESS_RULES#quotation-screen-money-labels):
                              Intl's own TTD style prints a bare "$" (QA 2026-10-01). `ceil: false`:
                              `calcTotalPriceTtd` already finished the figure; this only labels it. */}
                          {formatCurrency(row.totalPrice, 'TTD', { ceil: false })}
                          {/* D82: the one wording the detail page and the supplier's PDF use. */}
                          {row.priceBasis === 'before_vat' && (
                            <span className="block text-xs font-normal text-slate-500 whitespace-normal">
                              {PRICES_EXCLUDE_VAT}
                            </span>
                          )}
                          {/* TECH_DEBT #342 — a PO total silently excludes any line with no
                              agreed price yet (D54); say so here, the same words the PDF/
                              Excel/email use (unpricedLineNote), not a screen-local phrasing. */}
                          {(row.unpricedLineCount || 0) > 0 && (
                            <span className="block text-xs font-normal text-amber-700 whitespace-normal">
                              ({unpricedLineNote(row.unpricedLineCount || 0)})
                            </span>
                          )}
                        </td>
                        <td>
                          <StatusBadge status={row.status || 'draft'} />
                        </td>
                        {/* Delete only. There is deliberately NO pencil beside it:
                            it navigated to `/vendor-purchase-orders/${row._id}` —
                            the very same route as the View-details arrow in the next
                            column — so the two controls were one control drawn twice
                            (USC-BUG-0007).

                            The layout was borrowed from ClientPriceListsPage, where
                            the pencil earns its place by opening a MODAL
                            (`openItemsModal`) while the arrow navigates. This page has
                            no such second action: the PO detail page is a single
                            always-editable form with no view/edit mode to deep-link
                            into, so there is nothing for a second icon to mean.

                            The ARROW is the survivor, not the pencil, because the
                            arrow is ungated: a reader without `canEdit` still has to
                            be able to open a PO. Removing the arrow instead would
                            have taken the page away from them entirely. */}
                        <td className="sticky right-0 bg-white text-center">
                          <span className="inline-flex items-center justify-center gap-1">
                            {canDelete && (
                              <Button
                                size="sm"
                                variant="ghost"
                                aria-label="Delete"
                                disabled={deletingId === row._id}
                                loading={deletingId === row._id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPendingDelete(row);
                                }}
                                icon={<Trash2 aria-hidden="true" className="h-4 w-4" />}
                              />
                            )}
                            {/* Delete is now the only thing this cell can hold, so the
                                dash turns on `canDelete` alone. Left as `!canEdit &&
                                !canDelete` it would have rendered an empty cell for an
                                editor who cannot delete. */}
                            <Button
                            size="sm"
                            variant="ghost"
                            aria-label="View details"
                            onClick={() => navigate(`/vendor-purchase-orders/${row._id}`)}
                            icon={<ArrowRight aria-hidden="true" className="h-4 w-4" />}
                          />
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {!loading && !error && (
        <div className="shrink-0 bg-white">
          <Pagination page={page} pageSize={limit} total={total} onPageChange={setPage} noun="purchase orders" />
        </div>
      )}

      <VendorBatchSendModal isOpen={showByVendor} onClose={() => setShowByVendor(false)} />

      <ActionDialog
        isOpen={pendingDelete !== null}
        tone="error"
        title="Delete this vendor purchase order?"
        summary={
          pendingDelete
            ? `${pendingDelete.poNumber || 'This purchase order'} and its lines are removed. This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onClose={() => setPendingDelete(null)}
      />

      <ErrorModal
        isOpen={deleteError !== null}
        title="Delete failed"
        message={deleteError || ''}
        onClose={() => setDeleteError(null)}
      />

      <ErrorModal
        isOpen={assignError !== null}
        title="Could not set vendor"
        message={assignError || ''}
        onClose={() => setAssignError(null)}
      />

      <ActionDialog
        isOpen={assignNote !== null}
        tone="success"
        title="Vendor set - check this purchase order"
        summary={assignNote || ''}
        onClose={() => setAssignNote(null)}
      />
    </div>
  );
};
